import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { App, Button, Card, Space, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import DataTable from '../../components/DataTable';
import FilterPanel from '../../components/FilterPanel';
import EmptyState from '../../components/EmptyState';
import DateRangePicker, { type DateRange } from '../../components/DateRangePicker';
import { reportsApi } from '../../api/reports.api';
import { toApiError } from '../../api/client';
import type { ReportQuery } from '../../types/reports';
import AiAnalysisBadge from '../../components/AiAnalysisBadge';
import RoleGuard from '../../auth/RoleGuard';
import { REPORTS } from '../../constants/reports';

type Row = Record<string, unknown> & { __key: number };

/** Generic report renderer — one component drives every report type (columns come from the API). */
export default function ReportView({ reportType, title }: { reportType: string; title: string }) {
  const report = REPORTS.find((item) => item.type === reportType);
  return <RoleGuard allow={report?.roles ?? []}><ReportContent reportType={reportType} title={title} /></RoleGuard>;
}

const aiColumns = new Set(['avgScore', 'highRisk', 'reviewRequired', 'avgResponseQuality', 'risk', 'score', 'dissatisfaction', 'complaint']);

function ReportContent({ reportType, title }: { reportType: string; title: string }) {
  const { message } = App.useApp();
  const [query, setQuery] = useState<ReportQuery>({});
  const q = useQuery({ queryKey: ['report', reportType, query], queryFn: () => reportsApi.get(reportType, query) });

  const onDate = (range: DateRange) => setQuery({ from: range?.[0]?.toISOString(), to: range?.[1]?.toISOString() });

  const onExport = async () => {
    try {
      await reportsApi.exportCsv(reportType, query);
    } catch (err) {
      message.error(toApiError(err).message);
    }
  };

  const columns: ColumnsType<Row> = (q.data?.columns ?? []).map((c) => ({
    title: aiColumns.has(c.key) ? <Space size={4}>{c.label}<AiAnalysisBadge compact /></Space> : c.label,
    dataIndex: c.key,
    key: c.key,
    render: (v: unknown) => (v == null || v === '' ? '—' : String(v)),
  }));

  const data: Row[] = (q.data?.rows ?? []).map((r, i) => ({ ...r, __key: i }));

  return (
    <Card
      title={<Typography.Title level={4} style={{ margin: 0 }}>{q.data?.title ?? title}</Typography.Title>}
      extra={<Button onClick={onExport} disabled={data.length === 0}>خروجی CSV</Button>}
    >
      <FilterPanel onReset={() => setQuery({})}>
        <Space direction="vertical" size={4}>
          <Typography.Text type="secondary">بازه زمانی</Typography.Text>
          <DateRangePicker onChange={onDate} />
        </Space>
      </FilterPanel>

      {data.length === 0 && !q.isLoading ? (
        <EmptyState description="داده‌ای برای این گزارش در بازهٔ انتخابی وجود ندارد." />
      ) : (
        <DataTable<Row> rowKey="__key" columns={columns} dataSource={data} loading={q.isLoading} />
      )}
    </Card>
  );
}
